import { useCallback, useMemo } from 'react';

import { Piece, PieceAction } from '@apiContract';
import { useSettings } from '@hooks/useSettings';
import { MailPieceInboxType } from '@rdTypes/shared';
import _ from 'lodash';

import { MailpieceAction, mailpieceActions } from '../index';
import { useMailpieceActions } from './useMailpieceActions';

type BulkActionValidationResult = {
  tooltipText: string;
  disabled: boolean;
};

type BulkActionValidation = (mailpieces: Piece[]) => BulkActionValidationResult;

type BulkActionConfigItem = {
  action: PieceAction;
  nextAction: string;
  bulkActionValidation?: BulkActionValidation;
};

type BulkAction = MailpieceAction & {
  id: PieceAction;
  disabled: boolean;
  tooltipText: string;
};

const isRecipientShared = (mailpieces: Piece[]) => {
  const recipientIds = _.uniq(mailpieces.map((mailpiece) => mailpiece.recipient?.id));
  return recipientIds.length === 1 && !!recipientIds[0];
};

const validateSharedRecipient: BulkActionValidation = (mailpieces) => {
  const singleRecipient = isRecipientShared(mailpieces);

  return {
    tooltipText: !singleRecipient ? 'Selected items must have the same recipient' : '',
    disabled: !singleRecipient,
  };
};

const bulkActionsConfig: BulkActionConfigItem[] = [
  {
    action: PieceAction.RequestForward,
    nextAction: 'RequestForward',
  },

  {
    action: PieceAction.RequestRecycle,
    nextAction: 'RequestRecycle',
  },

  {
    action: PieceAction.RequestOpenAndScan,
    nextAction: 'RequestOpenAndScan',
  },

  {
    action: PieceAction.RequestMail,
    nextAction: 'RequestMail',
  },

  {
    action: PieceAction.RequestPickUp,
    nextAction: 'RequestPickUp',
  },

  {
    action: PieceAction.RequestShred,
    nextAction: 'RequestShred',
  },

  {
    action: PieceAction.RequestDelivery,
    nextAction: 'RequestDelivery',
  },

  {
    action: PieceAction.FinishRecycle,
    nextAction: 'FinishRecycle',
  },

  {
    action: PieceAction.FinishOpenAndScan,
    nextAction: 'FinishOpenAndScan',
    bulkActionValidation: (mailpieces) => ({
      tooltipText: mailpieces.length > 1 ? 'Only one item can be opened and scanned at a time' : '',
      disabled: mailpieces.length > 1,
    }),
  },

  {
    action: PieceAction.FinishMail,
    nextAction: 'FinishMail',
    bulkActionValidation: (mailpieces) => {
      const singleRecipient = isRecipientShared(mailpieces);
      const recipientAddresses = mailpieces.map((mailpiece) => {
        if (!mailpiece.mailTo) return null;

        const { countryCode, administrativeArea, locality, postalCode, addressLine1, addressLine2 } = mailpiece.mailTo;

        return { countryCode, administrativeArea, locality, postalCode, addressLine1, addressLine2 };
      });
      const singleAddress = recipientAddresses.every((address) => _.isEqual(address, recipientAddresses[0]));

      let tooltipText = '';

      if (!singleRecipient) {
        tooltipText = 'Selected items must have the same recipient';
      } else if (!singleAddress) {
        tooltipText = 'Selected items must have the same address';
      }

      return {
        tooltipText: tooltipText,
        disabled: !singleRecipient || !singleAddress,
      };
    },
  },

  {
    action: PieceAction.FinishPickUp,
    nextAction: 'FinishPickUp',
  },

  {
    action: PieceAction.FinishShred,
    nextAction: 'FinishShred',
  },

  {
    action: PieceAction.FinishPickUpWithSignature,
    nextAction: 'FinishPickUpWithSignature',
  },

  {
    action: PieceAction.FinishPickUpWithQrCode,
    nextAction: 'FinishPickUpWithQrCode',
    bulkActionValidation: validateSharedRecipient,
  },

  {
    action: PieceAction.FinishPickupWithIdCard,
    nextAction: 'FinishPickupWithIdCard',
  },

  {
    action: PieceAction.FinishDelivery,
    nextAction: 'FinishDelivery',
  },

  {
    action: PieceAction.FinishDeliveryWithSignature,
    nextAction: 'FinishDeliveryWithSignature',
  },

  {
    action: PieceAction.FinishDeliveryWithImage,
    nextAction: 'FinishDeliveryWithImage',
  },

  {
    action: PieceAction.FinishDeliveryWithQrCode,
    nextAction: 'FinishDeliveryWithQrCode',
    bulkActionValidation: validateSharedRecipient,
  },

  {
    action: PieceAction.Archive,
    nextAction: 'Archive',
  },

  {
    action: PieceAction.FinishPickupWithImage,
    nextAction: 'FinishPickupWithImage',
  },
  {
    action: PieceAction.FinishReturnToSender,
    nextAction: 'FinishReturnToSender',
  },
];

export const useGetBulkActionsConfig = (mailpieces: Piece[], access: MailPieceInboxType) => {
  const settings: any = useSettings();
  const { bulkRegularActions, allActions } = useMailpieceActions(mailpieces, access);

  const getAction = useCallback(
    ({ action, nextAction, bulkActionValidation }: BulkActionConfigItem): BulkAction | null => {
      const availableAsNormalAction = allActions.some((pieceActions) =>
        pieceActions.some((item) => item.pieceAction === nextAction && !item.isForce),
      );

      if (!availableAsNormalAction) {
        return null;
      }

      if (!bulkRegularActions.includes(action)) {
        return null;
      }

      const actionConfig = mailpieceActions[action];
      if (!actionConfig) return null;

      let tooltipText = '';
      let disabled = false;

      const actionIsShared = allActions.every((pieceActions) =>
        pieceActions.some((item) => item.pieceAction === nextAction && !item.isForce),
      );

      const validationRequired = !!(
        typeof actionConfig.validationSettingName === 'string' && settings[actionConfig.validationSettingName]
      );

      const bulkActionValidationResult = bulkActionValidation?.(mailpieces);

      if (!actionIsShared) {
        tooltipText = 'Not available for some selected items';
        disabled = true;
      } else if (validationRequired) {
        tooltipText = 'Only one item can be validated at a time';
        disabled = true;
      } else if (bulkActionValidationResult?.tooltipText) {
        tooltipText = bulkActionValidationResult.tooltipText;
        disabled = bulkActionValidationResult.disabled;
      }

      return {
        id: action,
        disabled,
        tooltipText,
        ...actionConfig,
      };
    },
    [bulkRegularActions, allActions, mailpieces, settings],
  );

  const availableActions = useMemo(() => {
    return bulkActionsConfig.reduce((acc: BulkAction[], actionConfig) => {
      const action = getAction(actionConfig);
      return action ? [...acc, action] : acc;
    }, []);
  }, [getAction]);

  return availableActions;
};
